import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { FaceTecIDScanResult, FaceTecSessionResult } from '@/common/contexts/types';
import { useNotification } from '@/components/notification';
import { ONBOARDING_SESSION_KEY } from '@/common/constants';
import { generateDiagnosticReport } from '@/common/utils/facetec-config-checker';
import {
  FaceTecSingleton,
  type BiometricCallback,
  type SDKStatus,
} from '@/common/utils/facetec-singleton';

/**
 * Modern FaceTec SDK integration hook with enhanced error handling.
 *
 * This hook provides a comprehensive interface for FaceTec biometric operations including:
 * - Automatic SDK initialization using ensureInitialized()
 * - Singleton pattern to prevent multiple SDK instances
 * - Detailed initialization states and error reporting
 * - Retry logic with automatic recovery
 * - Reactive language configuration
 * - Memory leak prevention with proper cleanup
 *
 * @param props - Hook configuration
 * @param props.callback - Callback function for biometric operation results
 *
 * @returns Object containing biometric operations and state
 * @returns doEnrollment - Perform face enrollment (compatible with original interface)
 * @returns doEnrollmentOcr - Perform document OCR enrollment (compatible with original interface)
 * @returns isInitialized - Whether SDK is fully initialized and ready
 * @returns isInitializing - Whether SDK is currently initializing
 * @returns error - Current error message or null
 * @returns sdkStatus - Detailed SDK status from getSDKStatus()
 * @returns lastResult - Last biometric operation result
 * @returns retryInitialization - Function to retry SDK initialization
 * @returns isSDKReady - Whether base SDK is loaded
 *
 * @example
 * ```tsx
 * const {
 *   doEnrollment,
 *   isInitialized,
 *   isInitializing,
 *   error,
 *   retryInitialization
 * } = useFaceAuth({
 *   callback: (result) => {
 *     if (result?.status === 0 && result?.isCompletelyDone) {
 *       // Success handling
 *     }
 *   }
 * });
 *
 * if (error) {
 *   return (
 *     <div>
 *       <p>Error: {error}</p>
 *       <button onClick={retryInitialization}>Retry</button>
 *     </div>
 *   );
 * }
 * ```
 */

interface BiometricResult {
  sessionResult: FaceTecSessionResult | null;
  idScanResult: FaceTecIDScanResult | null;
  status: number;
}

export const useFaceAuth = ({
  callback,
}: {
  callback: (result: FaceTecSessionResult | FaceTecIDScanResult) => void;
}) => {
  const callbackRef = useRef(callback);
  const { setError } = useNotification();
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language?.toLowerCase()?.replace('_', '-');

  const [lastResult] = useState<BiometricResult | null>(null);
  const [localStates, setLocalStates] = useState(() => FaceTecSingleton.getState());
  const [isSDKReady, setIsSDKReady] = useState(false);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const wrappedCallback: BiometricCallback = (result) => {
      if (callbackRef.current) {
        callbackRef.current(result);
      }
    };

    FaceTecSingleton.addCallback(wrappedCallback);

    return () => {
      FaceTecSingleton.removeCallback(wrappedCallback);
    };
  }, []);

  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const syncState = () => {
      const newState = FaceTecSingleton.getState();

      setLocalStates((prevState) => {
        const hasChanged =
          prevState.isInitialized !== newState.isInitialized ||
          prevState.isInitializing !== newState.isInitializing ||
          prevState.error !== newState.error ||
          prevState.sdkStatus !== newState.sdkStatus ||
          prevState.initializationAttempts !== newState.initializationAttempts ||
          prevState.lastFailureReason !== newState.lastFailureReason ||
          prevState.hasPermanentFailure !== newState.hasPermanentFailure;

        return hasChanged ? newState : prevState;
      });
    };

    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
      syncIntervalRef.current = null;
    }

    syncState();

    syncIntervalRef.current = setInterval(syncState, 1000);

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
    };
  }, []);

  const getDiagnosticMessage = useCallback((status: SDKStatus): string => {
    if (!status) return 'SDK status unavailable';

    if (!status.details) return `SDK not ready: ${status.status}`;

    const { details } = status;

    if (!details.environmentValid && details.environmentErrors?.length) {
      return `Environment issue: ${details.environmentErrors[0]}`;
    }

    if (!details.sdkLoaded) {
      return 'FaceTec SDK failed to load. Check network connection and try again.';
    }

    if (details.sdkStatusCode !== undefined) {
      switch (details.sdkStatusCode) {
        case -1:
          return 'SDK not loaded';
        case -2:
          return 'License key invalid or expired';
        case -3:
          return 'Device not supported';
        case -4:
          return 'SDK version deprecated';
        case 0:
          return 'SDK not initialized';
        default:
          return `SDK status: ${details.sdkStatusCode}`;
      }
    }

    return 'Unknown initialization error';
  }, []);

  const initializeSDK = useCallback(async () => {
    if (
      localStates.isInitializing ||
      !isSDKReady ||
      localStates.hasPermanentFailure ||
      localStates.isInitialized ||
      localStates.initializationAttempts >= 3
    ) {
      return;
    }

    try {
      const initialized = await FaceTecSingleton.ensureInitialized(getDiagnosticMessage);

      if (!initialized && localStates.initializationAttempts >= 2) {
        setError('try-again');
      }
    } catch (err: unknown) {
      console.error('FaceAuth initialization failed:', err);

      if (localStates.initializationAttempts >= 2) {
        setError('try-again');
      }
    }
  }, [
    isSDKReady,
    localStates.isInitializing,
    localStates.initializationAttempts,
    localStates.hasPermanentFailure,
    localStates.isInitialized,
    setError,
    getDiagnosticMessage,
  ]);

  const sdkCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const checkSDKLoaded = () => {
      return typeof window !== 'undefined' && 'FaceTecSDK' in window;
    };

    const initIfReady = async () => {
      const sdkLoaded = checkSDKLoaded();
      setIsSDKReady(sdkLoaded);

      const shouldInitialize =
        sdkLoaded &&
        !localStates.isInitialized &&
        !localStates.isInitializing &&
        !localStates.hasPermanentFailure &&
        localStates.initializationAttempts < 3;

      console.log('FaceAuth Debug:', {
        sdkLoaded,
        isInitialized: localStates.isInitialized,
        isInitializing: localStates.isInitializing,
        hasPermanentFailure: localStates.hasPermanentFailure,
        initializationAttempts: localStates.initializationAttempts,
        shouldInitialize,
      });

      if (shouldInitialize) {
        await initializeSDK();
      }
    };

    if (sdkCheckIntervalRef.current) {
      clearInterval(sdkCheckIntervalRef.current);
      sdkCheckIntervalRef.current = null;
    }

    initIfReady();

    if (!checkSDKLoaded()) {
      sdkCheckIntervalRef.current = setInterval(() => {
        if (checkSDKLoaded()) {
          if (sdkCheckIntervalRef.current) {
            clearInterval(sdkCheckIntervalRef.current);
            sdkCheckIntervalRef.current = null;
          }
          initIfReady();
        }
      }, 1000);
    }

    return () => {
      if (sdkCheckIntervalRef.current) {
        clearInterval(sdkCheckIntervalRef.current);
        sdkCheckIntervalRef.current = null;
      }
    };
  }, [
    localStates.isInitialized,
    localStates.isInitializing,
    localStates.hasPermanentFailure,
    initializeSDK,
  ]);

  useEffect(() => {
    const configureLanguage = async () => {
      if (localStates.isInitialized && currentLanguage !== 'en' && currentLanguage !== 'it') {
        const instance = await FaceTecSingleton.getInstance();
        if (instance && instance.configureLocalization) {
          instance.configureLocalization(currentLanguage).catch((err: unknown) => {
            console.warn('Language configuration failed:', err);
          });
        }
      }
    };

    configureLanguage();
  }, [localStates.isInitialized, currentLanguage]);

  const doEnrollment = useCallback(
    async (enrollmentIdentifier?: string) => {
      const instance = await FaceTecSingleton.getInstance();
      if (!localStates.isInitialized || !instance) {
        throw new Error('SDK not ready');
      }

      try {
        await instance.doEnrollment(enrollmentIdentifier);
      } catch (err: unknown) {
        setError('try-again');
        throw err;
      }
    },
    [localStates.isInitialized, setError]
  );

  const doEnrollmentOcr = useCallback(
    async (enrollmentIdentifier?: string) => {
      const instance = await FaceTecSingleton.getInstance();
      if (!localStates.isInitialized || !instance) {
        throw new Error('SDK not ready');
      }

      try {
        await instance.doEnrollmentOcr(enrollmentIdentifier);
      } catch (err: unknown) {
        setError('try-again');
        throw err;
      }
    },
    [localStates.isInitialized, setError]
  );

  const retryInitialization = useCallback(async () => {
    if (localStates.isInitializing) return;

    await FaceTecSingleton.retry(getDiagnosticMessage);
  }, [localStates.isInitializing, getDiagnosticMessage]);

  const getDiagnosticInfo = useCallback(() => {
    const basicInfo = {
      attempts: localStates.initializationAttempts,
      isSDKReady,
      isInitialized: localStates.isInitialized,
      isInitializing: localStates.isInitializing,
      error: localStates.error,
      lastFailureReason: localStates.lastFailureReason,
      sdkStatus: localStates.sdkStatus,
    };

    const fullReport = generateDiagnosticReport(ONBOARDING_SESSION_KEY);

    const diagnostics = {
      ...basicInfo,
      ...fullReport,
    };

    console.log('FaceTec Comprehensive Diagnostic Info:', diagnostics);
    return diagnostics;
  }, [
    localStates.initializationAttempts,
    isSDKReady,
    localStates.isInitialized,
    localStates.isInitializing,
    localStates.error,
    localStates.lastFailureReason,
    localStates.sdkStatus,
  ]);

  // Browser checking removed - no longer validating browser compatibility

  return {
    doEnrollment,
    doEnrollmentOcr,
    isInitialized: localStates.isInitialized,
    isInitializing: localStates.isInitializing,
    error: localStates.error,
    sdkStatus: localStates.sdkStatus,
    lastResult,
    retryInitialization,
    initializationAttempts: localStates.initializationAttempts,
    lastFailureReason: localStates.lastFailureReason,
    hasPermanentFailure: localStates.hasPermanentFailure,
    getDiagnosticInfo,
    isSDKReady,
  };
};
